import { AsyncPipe, DatePipe } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { IconComponent } from "@components/icon/icon.component";
import { InfoComponent } from "@components/info/info.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { RadiobuttonComponent } from "@components/radiobuttongroup/radiobutton/radiobutton.component";
import { NgbPopover, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import _, { isNil } from "lodash";
import {
    BehaviorSubject,
    Observable,
    combineLatest,
    map,
    of,
    shareReplay
} from "rxjs";
import { FastighetGeometry } from "~app/common/components/map/components/menu/map-fastighet/map-fastighet-models";
import { Anskaffningsformer } from "~app/common/models/constants";
import { IKontaktDTO, KontaktDTO } from "~app/common/models/kontakter/KontaktDTO";
import { MomsnummerValideringsstatus } from "~app/common/models/momsnummerValideringsstatus";
import { FeatureService } from "~app/common/services/feature.service";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { PhoneFormatter } from "../../utils/phone-formatter";
import { LeverantorDialogService } from "../../virkesleverantor/dialog/leverantor-dialog.service";
import { ILeverantor, ISearchVirkesleverantor, Leverantor, VirkesleverantorViewmodel } from "../../virkesleverantor/models/virkesleverantorModel";

export interface ILeverantorChangedEvent {
    changeType: LeverantorChangeType;
    leverantor: ILeverantor;
}

export type LeverantorChangeType = "update" | "remove" | "add";

@Component({
    selector: "rask-leverantorsinformation",
    templateUrl: "./leverantorsinformation.component.html",
    styleUrls: ["./leverantorsinformation.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        InfoComponent,
        ActionButtonComponent,
        ListComponent,
        ListColumnDirective,
        NgbTooltip,
        RadiobuttonComponent,
        IconButtonComponent,
        NgbPopover,
        InputNumberComponent,
        ButtonComponent,
        IconComponent,
        CheckboxComponent,
        AsyncPipe,
        DatePipe
    ]
})
export class LeverantorsinformationComponent implements OnInit {
    @Output() leverantorer$: BehaviorSubject<ILeverantor[]> = new BehaviorSubject<ILeverantor[]>([]);
    momsnummerValideringsstatus = MomsnummerValideringsstatus;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onFastighetSelected = new EventEmitter<FastighetGeometry>();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onLeverantorChanged = new EventEmitter<ILeverantorChangedEvent>();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onOpenCreateLeverantor = new EventEmitter();

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onOpenLeverantorSearch = new EventEmitter();
    @Input() readonly: boolean;
    @Input() writePermission: boolean;

    protected overstyrMatbeskedActive$: Observable<boolean>;

    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly featureService = inject(FeatureService);
    private readonly leverantorDialogService = inject(LeverantorDialogService);

    private leverantorer: ILeverantor[];

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("leverantorer") set list(leverantorer: ILeverantor[]) {
        if (!leverantorer) {
            leverantorer = [];
        }

        this.leverantorer = leverantorer;
        this.refreshViewList(this.leverantorer);
    }

    createLeverantor = () => {
        const leverantor = new Leverantor(new VirkesleverantorViewmodel(null));
        this.leverantorDialogService.openNew(leverantor, leverantor.guid, (kontakt) => {
            if (kontakt?.id) {
                this.mapLeverantorFromKontakt(leverantor, kontakt);
                leverantor.id = kontakt.id;
                leverantor.guid = kontakt.guid;

                this.leverantorer.push(leverantor);

                this.onLeverantorChanged.emit({
                    changeType: "add",
                    leverantor: leverantor
                });

                this.handleDefaultAndelForLeverantorer();

                this.refreshViewList(this.leverantorer);
            }
        });
    };

    getDisplayName(row: ILeverantor) {
        return row.namn ? row.namn : `${row.kontaktFornamn} ${row.kontaktEfternamn}`;
    }

    isOverstyrMatbeskedActive(leverantorsrad: ILeverantor): Observable<boolean> {
        if (!leverantorsrad.kontaktperson) {
            return of(false);
        }
        return this.overstyrMatbeskedActive$;
    }

    leverantorAdressFormatter(row: ILeverantor) {
        return [
            row.adress,
            row.adress2,
            row.postnummer,
            row.postort
        ].join(" ");
    }

    leverantorAndelFormatter(row: ILeverantor) {
        const andelTaljare = _.isNumber(row.andelTaljare) ? row.andelTaljare : "-";
        const andelNamnare = _.isNumber(row.andelNamnare) ? row.andelNamnare : "-";
        return `${andelTaljare} / ${andelNamnare}`;
    }

    leverantorPhoneFormatter(row: ILeverantor) {
        return PhoneFormatter.renderPhonePresentation([
            row.kontaktMobiltelefon,
            row.telefon
        ]);
    }

    momsnummerIcon = (rad: ILeverantor) => {
        if (rad.momsnummerValideringsstatus === MomsnummerValideringsstatus.ogiltig) {
            return "far fa-exclamation-circle";
        }
        if (rad.momsnummerValideringsstatus === MomsnummerValideringsstatus.ejValiderad) {
            return "far fa-exclamation-square";
        }
        return "far fa-exclamation-square";
    };

    momsnummerStatusText = (rad: ILeverantor) => {
        switch (rad.momsnummerValideringsstatus) {
            case this.momsnummerValideringsstatus.giltig:
                return "";
            case this.momsnummerValideringsstatus.ogiltig:
                return "Ogiltigt momsregistreringsnummer";
            case this.momsnummerValideringsstatus.ejValiderad:
                return "Kontroll kunde ej genomföras";
            default:
                return "Numret har ej kontrollerats";
        }
    };

    ngOnInit(): void {
        const isVirkeskopAnksaffningsForm$ = this.avtalMediatorService.avtal$.pipe(
            map(avtal => Anskaffningsformer.VirkeskopAnksaffningsFormer.includes(avtal.current.anskaffningsform))
        );

        this.overstyrMatbeskedActive$ = combineLatest([isVirkeskopAnksaffningsForm$]).pipe(
            map(([isVirkeskopAnksaffningsForm]) => isVirkeskopAnksaffningsForm),
            shareReplay(1)
        );
    }

    openRedigeraLeverantorDialog(virkesleverantorId?: number, guid?: string) {
        if (isNil(virkesleverantorId) || virkesleverantorId < 1) {
            const leverantor = this.findLeverantor(virkesleverantorId, guid);
            this.leverantorDialogService.openNew(leverantor, guid, kontakt => this.redigeraLeverantorDialogCallback(kontakt, guid));
        } else {
            this.leverantorDialogService.open(virkesleverantorId, guid, kontakt => this.redigeraLeverantorDialogCallback(kontakt, guid));
        }
    }

    redigeraLeverantorDialogCallback(kontakt: KontaktDTO, guid: string) {
        if (!kontakt || !this.leverantorer) {
            return;
        }
        const leverantor = this.findLeverantor(kontakt.id, guid);
        if (!!guid && kontakt.id) {
            leverantor.id = kontakt.id;
        }
        if (leverantor) {
            this.mapLeverantorFromKontakt(leverantor, kontakt);
            this.onLeverantorChanged.emit({
                changeType: "update",
                leverantor: leverantor
            });
        }

        this.refreshViewList(this.leverantorer);
    }

    removeLeverantor = (leverantorsrad: ILeverantor) => {
        const element = this.findLeverantor(leverantorsrad.id, leverantorsrad.guid);

        if (element) {
            const elementPos = this.leverantorer.indexOf(element);

            this.leverantorer.splice(elementPos, 1);
        }
        this.handleDefaultAndelForLeverantorer();

        this.onLeverantorChanged.emit({
            changeType: "remove",
            leverantor: leverantorsrad
        });

        this.refreshViewList(this.leverantorer);
    };

    searchFastighet = () => {
        this.leverantorDialogService.openSearch(
            { openTab: "Fastighetsregistret" },
            this.handleLeverantorSearchResult,
            this.handleFastighetSearchResult);
    };

    searchLeverantor = () => {
        this.leverantorDialogService.openSearch(
            { openTab: "Leverantörsregistret" },
            this.handleLeverantorSearchResult,
            this.handleFastighetSearchResult);
    };

    setAndel(leverantorsrad: ILeverantor, andelTaljare: number, andelNamnare: number, popover: NgbPopover = null) {
        leverantorsrad.andelTaljare = andelTaljare;
        leverantorsrad.andelNamnare = andelNamnare;

        this.onLeverantorChanged.emit({
            changeType: "update",
            leverantor: leverantorsrad
        });

        if (popover) {
            popover.close();
        }
    }

    setKontaktperson = (virkesleverantor: ILeverantor) => {
        this.leverantorer.forEach((leverantor: ILeverantor) => {
            const isKontaktperson = (!!leverantor.guid && leverantor.guid === virkesleverantor.guid) ||
                (!!leverantor.id && leverantor.id === virkesleverantor.id);
            leverantor.kontaktperson = isKontaktperson;
        });
        this.onLeverantorChanged.emit({
            changeType: "update",
            leverantor: virkesleverantor
        });

        this.refreshViewList(this.leverantorer);
    };

    setOverstyrMatbeskedsadress(virkesleverantor: ILeverantor, checked: boolean) {
        virkesleverantor.overstyrMatbeskedsadress = checked;
        this.onLeverantorChanged.emit({
            changeType: "update",
            leverantor: virkesleverantor
        });

        this.refreshViewList(this.leverantorer);
    }

    private findLeverantor(virkesleverantorId?: number, guid?: string): ILeverantor {
        return this.leverantorer
            .find(lev => (!!lev.id && lev.id === virkesleverantorId) ||
                (!!lev.guid && lev.guid === guid));
    }

    private refreshViewList(leverantorer: ILeverantor[]) {
        this.leverantorer = [...leverantorer]; // force new collection
        this.leverantorer$.next(leverantorer);
    }

    private readonly handleDefaultAndelForLeverantorer = () => {
        if (!this.leverantorer) {
            return;
        }

        if (this.leverantorer.length === 1) {
            this.setAndel(this.leverantorer[0], 1, 1);
        } else if (this.leverantorer?.length > 1) {
            this.leverantorer.forEach(leverantor => {
                if (leverantor.andelNamnare === 1 && leverantor.andelTaljare === 1) {
                    this.setAndel(this.leverantorer[0], undefined, undefined);
                }
            });
        }

        this.refreshViewList(this.leverantorer);
    };

    private readonly handleFastighetSearchResult = (result: FastighetGeometry) => {
        this.onFastighetSelected.emit(result);
    };

    private readonly handleLeverantorSearchResult = (leverantorer: ISearchVirkesleverantor[]) => {
        leverantorer
            .filter(search => this.leverantorer.every(l => l.id !== search.id)) // filter out already added leverantorer
            .forEach(search => {
                const leverantor = new Leverantor();
                leverantor.id = search.id;
                leverantor.guid = search.guid;
                leverantor.adress = search.adress;
                leverantor.namn = search.namn;
                leverantor.persOrgNummer = search.orgnr;
                leverantor.levNummer = search.limeId;
                leverantor.kontaktFornamn = search.fornamn;
                leverantor.kontaktEfternamn = search.efternamn;
                leverantor.telefon = search.telefon;
                leverantor.betalningar = [];
                leverantor.kontaktMobiltelefon = search.mobil;
                leverantor.momskod = search.momskod;
                leverantor.momsnummerKontrollerat = search.momsnummerKontrollerat;
                leverantor.momsnummerValideringsstatus = search.momsnummerValideringsstatus;
                leverantor.momsnummerValideringstid = search.momsnummerValideringstid;
                leverantor.momsnummerEjAktuellt = search.momsnummerEjAktuellt;
                leverantor.momsregistreringsnummer = search.momsregistreringsnummer;
                leverantor.anskaffningsform = search.anskaffningsform;
                leverantor.epost = search.epost;

                this.leverantorer.push(leverantor);

                this.onLeverantorChanged.emit({
                    changeType: "add",
                    leverantor: leverantor
                });
                this.handleDefaultAndelForLeverantorer();
            });
        this.refreshViewList(this.leverantorer);
    };

    private mapLeverantorFromKontakt(leverantor: ILeverantor, kontakt: IKontaktDTO) {
        leverantor.namn = kontakt.kontaktNamn;
        leverantor.guid = kontakt.guid;
        leverantor.kontaktFornamn = kontakt.kontaktNamn;
        leverantor.kontaktFornamn = kontakt.kontaktpersonFornamn;
        leverantor.kontaktEfternamn = kontakt.kontaktpersonEfternamn;
        leverantor.persOrgNummer = kontakt.kontaktOrgnummer;
        leverantor.levNummer = kontakt.kontaktLimeId;
        leverantor.telefon = kontakt.kontaktTelefon;
        leverantor.kontaktMobiltelefon = kontakt.kontaktKontaktMobiltelefon;
        leverantor.epost = kontakt.kontaktKontaktEpost;
        leverantor.adress = kontakt.kontaktAdress;
        leverantor.momskod = kontakt.momskod;
        leverantor.momsregistreringsnummer = kontakt.momsregistreringsnummer;
        leverantor.momsnummerKontrollerat = kontakt.momsnummerKontrollerat;
        leverantor.momsnummerValideringsstatus = kontakt.momsnummerValideringsstatus;
        leverantor.momsnummerValideringstid = kontakt.momsnummerValideringstid;
        leverantor.momsnummerEjAktuellt = kontakt.momsnummerEjAktuellt;
        leverantor.anskaffningsform = kontakt.anskaffningsform;
    }
}
